import { Autocomplete, Box, Button, CircularProgress, Divider, Drawer, Grid, IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Ticket } from "../../../../types/slices/ticketType";
import { formatDate } from "../../../../toolbox/helpers/formatDateHelper";
import CustomTable from "../../../../components/common/CustomTable/CustomTable";
import { moneyCL } from "../../../../toolbox/helpers/money-helpers";
import React, { useEffect, useState } from "react";
import { Tabs, Tab } from "@mui/material";
import { useBussinesAreaStore } from "../../../../hooks/useBussinesAreaStore";
import { ApiStatus } from "../../../../types/api/status";
import { bussinesArea } from "../../../../types/slices/bussinesAreaType";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
interface Props {
    openEmitModal: boolean;
    handleCloseDrawer: () => void;
    selectedTickets: Ticket[];
    readOnly: boolean;
    setSelectedOrders: React.Dispatch<React.SetStateAction<number[]>>;
    confirmEmitOrders: (references: ProcessOrder[]) => Promise<void>;
    processing: boolean
}

interface ProcessOrder {
    ticket: Ticket;
    nInterno: string;
    ordenCompra: string;
    references: Reference[];
    bussines_area?: bussinesArea
    folio?: string
}

export interface Reference {
    type: string;
    date: string;
    folio: string;
    reason?: string;
}


const StyledTableCell = ({ children, sx = {}, ...props }) => (
    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: 14, padding: '8px', ...sx }} {...props}>
        {children}
    </TableCell>
)


const HeaderCell = ({ children }) => (
    <StyledTableCell sx={{ background: '#69ABC3', color: '#fff', fontWeight: 'bold', textAlign: 'left' }}>
        {children}
    </StyledTableCell>
)

const columns = [
    { type: 'text', field: 'code_system', label: 'Código', align: 'center' },
    { type: 'text', field: 'product', label: 'Producto', align: 'center' },
    { type: 'text', field: 'unitmeasure_abr', label: 'Und. Med.', align: 'center' },
    { type: 'text', field: 'amount', label: 'Cantidad', align: 'center' },
    { type: 'text', field: 'price', label: 'Precio', align: 'right', format: (row) => moneyCL(row.price) },
    { type: 'text', field: 'net_subtotal', label: 'Descuento ($)', align: 'right', format: (row) => moneyCL((row.price * row.amount) - row.net_subtotal) },
    { type: 'text', field: 'net_subtotal', label: 'Total', align: 'right', format: (row) => moneyCL(row.net_subtotal) },
]

const referenceTypes = [
    { value: "HES", label: "HES" },
    { value: "OT", label: "OT" },
    { value: "CON", label: "CON" },
];

export const IssueDrawer: React.FC<Props> = (props) => {
    const { openEmitModal, handleCloseDrawer, selectedTickets, readOnly, confirmEmitOrders, processing, setSelectedOrders } = props;
    const [references, setReferences] = useState<Reference[]>([]);
    const [errors, setErrors] = useState<{ [key: number]: { folio: boolean; type: boolean; date: boolean; reason: boolean } }>({});
    const [orders, setOrders] = useState<ProcessOrder[]>([]);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedBussines, setSelectedBussines] = useState<any>(null)

    const { status: statusBussines, bussinesArea, getBussinesArea } = useBussinesAreaStore()

    const PAGE_SIZE = 5;
    const totalPages = Math.max(1, Math.ceil(orders.length / PAGE_SIZE));
    const safeActive = Math.min(activeTab, Math.max(orders.length - 1, 0));
    const page = Math.floor(safeActive / PAGE_SIZE);
    const start = page * PAGE_SIZE;
    const end = Math.min(start + PAGE_SIZE, orders.length);
    const visibleOrders = orders.slice(start, end);

    useEffect(() => {
        if (activeTab > orders.length - 1) {
            setActiveTab(Math.max(0, orders.length - 1));
        }
    }, [orders.length]);

    const goPrevPage = () => {
        const prevFirst = Math.max(0, (page - 1) * PAGE_SIZE);
        setActiveTab(prevFirst);
    };

    const goNextPage = () => {
        const nextFirst = Math.min(orders.length - 1, (page + 1) * PAGE_SIZE);
        setActiveTab(nextFirst);
    };

    useEffect(() => {
        setOrders(selectedTickets.map(ticket => ({
            ticket,
            nInterno: ticket?.n_internal ?? "",
            ordenCompra: ticket?.purchase_order ?? "",
            references: []
        })));
    }, [selectedTickets]);

    useEffect(() => {
        getBussinesArea()
    }, [])

    const handleUpdateOrder = (index: number, field: keyof ProcessOrder, value: any) => {
        setOrders(prevOrders =>
            prevOrders.map((order, i) =>
                i === index ? { ...order, [field]: value } : order
            )
        );
    };

    const handleUpdateReference = (refIndex: number, field: keyof Reference, value: any) => {
        setOrders(prevOrders =>
            prevOrders.map((order, i) =>
                i === activeTab
                    ? {
                        ...order,
                        references: order.references.map((ref, j) =>
                            j === refIndex ? { ...ref, [field]: value } : ref
                        )
                    }
                    : order
            )
        );
    };

    const handleAddReference = () => {
        setOrders(prevOrders =>
            prevOrders.map((order, i) =>
                i === activeTab
                    ? {
                        ...order,
                        references: [...order.references, { type: "HES", date: "", folio: "", reason: "" }]
                    }
                    : order
            )
        );
    };


    const handleRemoveReference = (refIndex: number) => {
        setOrders(prevOrders =>
            prevOrders.map((order, i) =>
                i === activeTab
                    ? { ...order, references: order.references.filter((_, j) => j !== refIndex) }
                    : order
            )
        );
    };

    const handleConfirm = () => {
        let newErrors: { [key: number]: { folio: boolean; type: boolean; date: boolean; reason: boolean } } = {};

        references.forEach((ref, index) => {
            newErrors[index] = {
                folio: !ref.folio.trim(),
                type: !ref.type,
                date: !ref.date,
                reason: !ref.reason
            };
        });

        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some(error => error.folio || error.type || error.date || error.reason);

        if (hasErrors) return;

        confirmEmitOrders(orders)
    };

    const clearData = () => {
        setReferences([]);
        setErrors({})
        setSelectedOrders([])
    }



    return (

        <Drawer
            anchor="right"
            open={openEmitModal}
            onClose={() => { clearData(); handleCloseDrawer() }}
        >
            <Grid container sx={{ width: 850, padding: 2 }} spacing={2}>

                <IconButton
                    onClick={() => { clearData(); handleCloseDrawer() }}
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Grid item xs={12}>
                    <Typography variant="h6">
                        Detalle del pedido
                    </Typography>
                </Grid>
                <Grid item xs={12} mb={2}>
                    <Divider />
                </Grid>

                {orders.length > 1 && (
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                                size="small"
                                onClick={goPrevPage}
                                disabled={page === 0}
                            >
                                <ChevronLeftIcon />
                            </IconButton>

                            <Tabs
                                value={activeTab}                
                                onChange={(_, newValue) => setActiveTab(newValue)}
                                variant="standard"
                                sx={{ flex: 1, minHeight: 48 }}
                            >
                                {visibleOrders.map((_, idx) => {
                                    const globalIndex = start + idx;  
                                    return (
                                        <Tab
                                            key={globalIndex}
                                            value={globalIndex}            
                                            label={`Documento ${globalIndex + 1}`}
                                            sx={{ minHeight: 48 }}
                                        />
                                    );
                                })}
                            </Tabs>

                            <IconButton
                                size="small"
                                onClick={goNextPage}
                                disabled={page >= totalPages - 1}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </Box>

                        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 5 }}>
                            {`Mostrando ${start + 1}–${end} de ${orders.length}`}
                        </Typography>
                    </Grid>
                )}


                <Grid item xs={12}>
                    {(() => {
                        const order = orders[activeTab];
                        return (
                            <>
                                <Paper elevation={0} sx={{ padding: 3, background: "#f5f5f5" }}>
                                    <Grid item container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableBody>
                                                        <TableRow>
                                                            <HeaderCell>N° Ticket</HeaderCell>
                                                            <StyledTableCell align="left">{order?.ticket?.nro_ticket}</StyledTableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <HeaderCell>Fecha</HeaderCell>
                                                            <StyledTableCell align="left">{formatDate(order?.ticket?.date || '')}</StyledTableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <HeaderCell>Documento</HeaderCell>
                                                            <StyledTableCell align="left">{order?.ticket?.type_document}</StyledTableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <HeaderCell>Forma de pago</HeaderCell>
                                                            <StyledTableCell align="left">{order?.ticket?.way_to_pay}</StyledTableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <HeaderCell>Estado de Pago</HeaderCell>
                                                            <StyledTableCell align="left">{order?.ticket?.payment_status_name}</StyledTableCell>
                                                        </TableRow>
                                                        {order?.ticket?.type_document && (
                                                            <TableRow>
                                                                <HeaderCell>Tipo de documento</HeaderCell>
                                                                <StyledTableCell align="left">{order?.ticket?.type_document}</StyledTableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableBody>
                                                        <TableRow>
                                                            <HeaderCell>Cliente</HeaderCell>
                                                            <StyledTableCell align="left">{order?.ticket?.name_customer}</StyledTableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <HeaderCell>RUT</HeaderCell>
                                                            <StyledTableCell align="left">{order?.ticket?.rut_customer}</StyledTableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <HeaderCell>Giro</HeaderCell>
                                                            <StyledTableCell align="left">{order?.ticket?.giro_customer}</StyledTableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <HeaderCell>Dirección</HeaderCell>
                                                            <StyledTableCell align="left">{`${order?.ticket?.client_address}, ${order?.ticket?.district_customer}, ${order?.ticket?.province_customer}`}</StyledTableCell>
                                                        </TableRow>
                                                        {order?.ticket?.folio && (
                                                            <TableRow>
                                                                <HeaderCell>Folio</HeaderCell>
                                                                <StyledTableCell align="left">{order?.ticket?.folio}</StyledTableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={2}>
                                        <CustomTable
                                            columns={columns}
                                            rows={order?.ticket?.details || []}
                                            withoutPagination
                                            onRowClick={() => null}
                                            dense
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={5} sx={{ marginLeft: "auto", marginTop: "30px" }}>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableBody>
                                                    <TableRow>
                                                        <HeaderCell>Subtotal</HeaderCell>
                                                        <StyledTableCell align="right">{moneyCL(order?.ticket?.sub_total || 0)}</StyledTableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <HeaderCell>Descuento</HeaderCell>
                                                        <StyledTableCell align="right">{moneyCL(order?.ticket?.total_discount || 0)}</StyledTableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <HeaderCell>Monto Neto</HeaderCell>
                                                        <StyledTableCell align="right">{moneyCL(order?.ticket?.net_total || 0)}</StyledTableCell>
                                                    </TableRow>
                                                    {order?.ticket?.idtype_document === 2 && (
                                                        <TableRow>
                                                            <HeaderCell>Monto Exento</HeaderCell>
                                                            <StyledTableCell align="right">{moneyCL(0)}</StyledTableCell>
                                                        </TableRow>
                                                    )}
                                                    <TableRow>
                                                        <HeaderCell>19% IVA</HeaderCell>
                                                        <StyledTableCell align="right">{moneyCL(order?.ticket?.IVA || 0)}</StyledTableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <HeaderCell>TOTAL</HeaderCell>
                                                        <StyledTableCell align="right" sx={{ fontWeight: "bold", fontSize: 16 }}>
                                                            {moneyCL(order?.ticket?.gross_total || 0)}
                                                        </StyledTableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>

                                    {!readOnly && (<>
                                        {!order?.ticket?.bussines_area && (
                                            <Grid item xs={12} sx={{ marginTop: 2, marginBottom: 2 }}>
                                                <Divider>Datos faltantes</Divider>
                                            </Grid>
                                        )}

                                        <Grid container xs={12} spacing={2}>

                                            {!order?.ticket?.bussines_area && (
                                                <Grid item xs={12} sm={6}>
                                                    <Autocomplete
                                                        disablePortal
                                                        id={`combo-box-bussines-area-${activeTab}`}
                                                        value={orders[activeTab]?.bussines_area || null}
                                                        options={statusBussines === ApiStatus.FETCHING ? [] : bussinesArea}
                                                        getOptionLabel={(option) => option?.name || ""}
                                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                                        fullWidth
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Área de negocio"
                                                                size="small"
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    endAdornment: (
                                                                        <>
                                                                            {statusBussines === ApiStatus.FETCHING && (
                                                                                <CircularProgress color="inherit" size={20} />
                                                                            )}
                                                                            {params.InputProps.endAdornment}
                                                                        </>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                        onChange={(_, newValue) => {
                                                            handleUpdateOrder(activeTab, 'bussines_area', newValue);
                                                        }}
                                                    />
                                                </Grid>)}
                                        </Grid>

                                        <Grid item xs={12} sx={{ marginTop: 2 }}>
                                            <Divider>Datos adicionales</Divider>
                                        </Grid>
                                        <Grid container spacing={2} sx={{ marginTop: 2 }}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label="N° Interno"
                                                    value={orders[activeTab]?.nInterno ?? ""}
                                                    onChange={(e) => handleUpdateOrder(activeTab, "nInterno", e.target.value)}
                                                    fullWidth
                                                    margin="dense"
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label="Orden de Compra"
                                                    value={orders[activeTab]?.ordenCompra ?? ""}
                                                    onChange={(e) => handleUpdateOrder(activeTab, "ordenCompra", e.target.value)}
                                                    fullWidth
                                                    margin="dense"
                                                    size="small"
                                                />
                                            </Grid>
                                        </Grid>


                                        {order?.ticket?.idtype_document === 2 && (
                                            <>
                                                <Grid item xs={12} sx={{ marginTop: 2 }}>
                                                    <Divider>Referencias</Divider>
                                                </Grid>

                                                {order.references.map((ref, refIndex) => (
                                                    <Grid container spacing={1} alignItems="center" key={refIndex} mt={1}>
                                                        <Grid item xs={3}>
                                                            <Select
                                                                fullWidth
                                                                value={orders[activeTab].references[refIndex]?.type || ""}
                                                                onChange={(e) => handleUpdateReference(refIndex, "type", e.target.value)}
                                                                size="small"
                                                            >
                                                                <MenuItem value="HES">HES</MenuItem>
                                                                <MenuItem value="OT">OT</MenuItem>
                                                                <MenuItem value="CON">CON</MenuItem>
                                                            </Select>

                                                        </Grid>
                                                        <Grid item xs={2.5}>
                                                            <TextField
                                                                label="Folio"
                                                                value={orders[activeTab].references[refIndex]?.folio || ""}
                                                                onChange={(e) => handleUpdateReference(refIndex, "folio", e.target.value)}
                                                                fullWidth
                                                                size="small"
                                                            />

                                                        </Grid>
                                                        <Grid item xs={2.5}>
                                                            <TextField
                                                                type="date"
                                                                value={orders[activeTab].references[refIndex]?.date || ""}
                                                                onChange={(e) => handleUpdateReference(refIndex, "date", e.target.value)}
                                                                fullWidth
                                                                size="small"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <TextField
                                                                label="Razón"
                                                                value={orders[activeTab].references[refIndex]?.reason || ""}
                                                                onChange={(e) => handleUpdateReference(refIndex, "reason", e.target.value)}
                                                                fullWidth
                                                                size="small"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={1}>
                                                            <IconButton onClick={() => handleRemoveReference(refIndex)} size="small" color="error">
                                                                ❌
                                                            </IconButton>
                                                        </Grid>
                                                    </Grid>
                                                ))}
                                                <Button variant="outlined" onClick={() => handleAddReference()} fullWidth sx={{ mt: 1 }}>
                                                    Agregar Referencia
                                                </Button>
                                            </>
                                        )}
                                    </>)}
                                </Paper>
                            </>
                        );
                    })()}
                </Grid>

                {!readOnly && (
                    <Grid container justifyContent="flex-end" spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item>
                            <Button onClick={() => { clearData(); handleCloseDrawer() }} variant="outlined">
                                Cancelar
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={() => handleConfirm()}
                                color="primary"
                                variant="contained"
                                disabled={processing}
                            >
                                {processing ? "Procesando..." : "Confirmar"}
                            </Button>

                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Drawer>
    )
}